import React, { useState } from 'react';
import { ItemModel } from '@/models/item.model';
import { useCart, Extra } from '@/contexts/cart.context';
import ImageWithFallback, { ImageSkeleton } from '../ImageWithFallback';
import {
  TrashIcon,
  MinusCircleIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';

interface Props {
  item: ItemModel | null;
  mode: 'list' | 'cart';
  className?: string;
  readOnly?: boolean;
  cartItemExtras?: Extra[];
}

function ItemCard({ item, mode, className, readOnly, cartItemExtras }: Props) {
  const { dispatch, state } = useCart();
  const [showExtrasModal, setShowExtrasModal] = useState(false);
  const [selectedExtras, setSelectedExtras] = useState<Extra[]>([]);

  // Helper to compare extras
  const areExtrasEqual = (extras1?: Extra[], extras2?: Extra[]): boolean => {
    if (!extras1 && !extras2) return true;
    if (!extras1 || !extras2) return false;
    if (extras1.length !== extras2.length) return false;

    const sorted1 = [...extras1].sort((a, b) => a.name.localeCompare(b.name));
    const sorted2 = [...extras2].sort((a, b) => a.name.localeCompare(b.name));

    return sorted1.every(
      (extra, i) =>
        extra.name === sorted2[i].name && extra.price === sorted2[i].price
    );
  };

  // Find matching cart item - in cart mode use cartItemExtras, in list mode don't
  const cartItem = state.items.find((x) => {
    if (mode === 'cart') {
      // In cart mode, match both ID and the passed extras
      return (
        x.item._id === item?._id &&
        areExtrasEqual(x.selectedExtras, cartItemExtras)
      );
    } else {
      // In list mode, just match items without extras
      return x.item._id === item?._id && !x.selectedExtras?.length;
    }
  });

  const currentCartItem = cartItem;
  const cartQuantity = currentCartItem?.quantity;

  // Calculate total price including extras
  const calculatePrice = (basePrice: number, extras?: Extra[]) => {
    const extrasPrice =
      extras?.reduce((sum, extra) => sum + extra.price, 0) || 0;
    return basePrice + extrasPrice;
  };

  const handleAddToCart = () => {
    if (!item) return;

    const itemExtras = (item as any).extras as Extra[] | undefined;

    if (itemExtras && itemExtras.length > 0) {
      setSelectedExtras([]);
      setShowExtrasModal(true);
    } else {
      dispatch({
        payload: { item, quantity: 1 },
        type: 'ADD_ITEM',
      });
    }
  };

  const handleConfirmExtras = () => {
    if (!item) return;

    dispatch({
      payload: {
        item,
        quantity: 1,
        selectedExtras: selectedExtras.length > 0 ? selectedExtras : undefined,
      },
      type: 'ADD_ITEM',
    });
    toast.success('Item added to order');
    setShowExtrasModal(false);
    setSelectedExtras([]);
  };

  const toggleExtra = (extra: Extra) => {
    setSelectedExtras((prev) => {
      const exists = prev.find((e) => e.name === extra.name);
      if (exists) {
        return prev.filter((e) => e.name !== extra.name);
      } else {
        return [...prev, extra];
      }
    });
  };

  const handleIncrement = () => {
    if (!item) return;
    dispatch({
      type: 'INCREMENT_ITEM',
      payload: {
        id: item._id,
        extras: cartItemExtras,
      },
    });
  };

  const handleDecrement = () => {
    if (!item || !cartQuantity) return;
    dispatch({
      type: cartQuantity > 1 ? 'DECREMENT_ITEM' : 'REMOVE_ITEM',
      payload: {
        id: item._id,
        extras: cartItemExtras,
      },
    });
  };

  const itemPrice = item
    ? calculatePrice(item.price, currentCartItem?.selectedExtras)
    : 0;

  return (
    <>
      <div
        className={`card card-side flex flex-wrap justify-center transition-all duration-200 ${
          item && mode === 'list'
            ? 'border-2 border-primary cursor-pointer hover:shadow-2xl hover:border-primary-focus hover:bg-primary/10'
            : ''
        } ${className ?? ''}`}
        onClick={mode === 'list' ? handleAddToCart : undefined}
      >
        <div>
          {!item ? (
            <ImageSkeleton className="rounded-md p-1 w-36" />
          ) : (
            <ImageWithFallback
              className="rounded-md p-1"
              src={item.imageUrl}
              alt={item.name}
              width={155}
              height={155}
              unoptimized
              unselectable="on"
            />
          )}
        </div>
        <div className="card-body w-full m-1 p-1 gap-1 flex justify-between flex-1">
          <div className="flex flex-col gap-1">
            <h2
              className={`card-title font-bold text-base flex ${
                !item && 'skeleton h-4'
              }`}
            >
              {item?.name}
            </h2>
            <p
              className={`text-sm line-clamp-2 ${!item && 'skeleton h-4'}`}
              title={item?.description}
            >
              {item?.description}
            </p>

            {mode === 'cart' &&
              currentCartItem?.selectedExtras &&
              currentCartItem.selectedExtras.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {currentCartItem.selectedExtras.map((extra, idx) => (
                    <span key={idx} className="badge badge-sm badge-outline">
                      {extra.name} +${extra.price.toFixed(2)}
                    </span>
                  ))}
                </div>
              )}
          </div>

          <div className="flex flex-col items-end justify-end gap-2">
            <div
              className={`font-bold text-sm text-nowrap ${
                !item && 'skeleton h-4 w-full'
              }`}
            >
              {item &&
                (mode === 'list'
                  ? `Price: $ ${item.price.toFixed(2)}`
                  : `Total: $ ${(itemPrice * (cartQuantity ?? 0)).toFixed(2)}`)}
            </div>
            {item && mode === 'cart' && (
              <div className="flex items-center gap-1">
                {!readOnly && (
                  <button
                    className="btn btn-sm btn-circle btn-ghost"
                    onClick={handleDecrement}
                    disabled={!cartQuantity}
                  >
                    <MinusCircleIcon
                      height={24}
                      className={
                        cartQuantity && cartQuantity === 1
                          ? 'text-error-content-soft'
                          : ''
                      }
                    />
                  </button>
                )}
                {readOnly && <span className="font-bold">Qt </span>}
                {cartQuantity ?? 0}
                {!readOnly && (
                  <button
                    className="btn btn-sm btn-circle btn-ghost"
                    onClick={handleIncrement}
                  >
                    <PlusCircleIcon height={24} />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {item && showExtrasModal && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              Select Extras for {item.name}
            </h3>

            <div className="space-y-2">
              {(((item as any).extras as Extra[]) || []).map((extra, idx) => {
                const isSelected = selectedExtras.some(
                  (e) => e.name === extra.name
                );
                return (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/10'
                        : 'border-base-300 hover:border-primary/50'
                    }`}
                    onClick={() => toggleExtra(extra)}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleExtra(extra)}
                        className="checkbox checkbox-primary checkbox-sm"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="font-medium">{extra.name}</span>
                    </div>
                    <span className="font-bold text-primary">
                      +${extra.price.toFixed(2)}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-between items-center mt-6 pt-4 border-t">
              <div className="text-sm">
                <div className="font-bold">Total Price:</div>
                <div className="text-xl font-bold text-primary">
                  ${calculatePrice(item.price, selectedExtras).toFixed(2)}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    setShowExtrasModal(false);
                    setSelectedExtras([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleConfirmExtras}
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop">
            <button
              onClick={() => {
                setShowExtrasModal(false);
                setSelectedExtras([]);
              }}
            >
              close
            </button>
          </form>
        </dialog>
      )}
    </>
  );
}

export default ItemCard;

export const ItemCardSkeleton = () =>
  ItemCard({ item: null, mode: 'list', className: 'h-40' });
